import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { streamChat, getChatSessionId, api } from "../lib/api";

const suggestions = ["NIST CSF vs ISO 27001?", "Explain the SEC 4-day rule", "Where do I start?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const sessionId = getChatSessionId();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing, open]);

  // Load persisted history when opened
  useEffect(() => {
    if (!open || loaded) return;
    api.get(`/chat/${sessionId}`).then((r) => {
      const hist = (r.data.messages || []).map((m) => ({ role: m.role, text: m.text }));
      setMessages(hist.length ? hist : [{ role: "assistant", text: "Howdy! I'm Sam — your AI risk advisor. Ask me about frameworks, US regulators, money flow, or where to start." }]);
      setLoaded(true);
    }).catch(() => {
      setMessages([{ role: "assistant", text: "Howdy! I'm Sam — your AI risk advisor. Ask me about frameworks, US regulators, or where to start." }]);
      setLoaded(true);
    });
  }, [open, loaded, sessionId]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput("");
    setTyping(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      await streamChat(sessionId, msg, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
          return copy;
        });
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sam hit a snag reaching his brain — please try again in a moment." };
        return copy;
      });
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-fab" aria-label="Ask Sam"
        className={`fixed bottom-5 left-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full text-white text-[14px] font-semibold shadow-lg rp-btn ${open ? "bg-[#14130F]" : "bg-[#12B76A] hover:bg-[#0E9355] rp-chat-fab"}`}>
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        {open ? "Close" : "Ask Sam"}
      </button>

      <a href="#newsletter" data-testid="alerts-pill" className="hidden md:flex fixed bottom-5 right-5 z-40 items-center gap-2 px-4 py-3 rounded-full bg-[#12B76A] hover:bg-[#0E9355] text-white text-[13.5px] font-semibold shadow-lg rp-btn">
        <MessageCircle size={16} /> Join: US Risk Alerts
      </a>

      {open && (
        <div className="fixed bottom-20 left-5 z-50 w-[min(400px,calc(100vw-40px))] h-[540px] bg-white border border-[#E7E0D2] rounded-2xl shadow-2xl flex flex-col overflow-hidden rp-fade-up" style={{ animationDuration: "0.3s" }} data-testid="chat-panel">
          <div className="px-5 py-4 bg-[#14130F] text-white flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#12B76A] flex items-center justify-center"><Sparkles size={17} /></div>
            <div>
              <p className="font-semibold text-[15px] leading-tight">Sam — AI Risk Advisor</p>
              <p className="text-[11.5px] text-white/50">Claude-powered · educational guidance, not legal advice</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF7F1]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#3F4CE0] text-white rounded-br-sm" : "bg-white border border-[#E7E0D2] text-[#1A1914] rounded-bl-sm"}`}>
                  {m.text || (typing && i === messages.length - 1 ? <span className="inline-flex gap-1.5 py-1"><span className="rp-dot w-1.5 h-1.5 rounded-full bg-[#1A1914]/60" /><span className="rp-dot w-1.5 h-1.5 rounded-full bg-[#1A1914]/60" /><span className="rp-dot w-1.5 h-1.5 rounded-full bg-[#1A1914]/60" /></span> : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="px-4 pt-2 pb-1 flex flex-wrap gap-1.5 bg-white border-t border-[#E7E0D2]">
            {suggestions.map((s) => (
              <button key={s} onClick={() => send(s)} disabled={typing} className="px-2.5 py-1 text-[11.5px] text-[#3F4CE0] bg-[#3F4CE0]/5 border border-[#3F4CE0]/20 rounded-full hover:bg-[#3F4CE0]/10 rp-btn disabled:opacity-40">{s}</button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 bg-white flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about frameworks, regulators…" data-testid="chat-input"
              className="flex-1 px-3.5 py-2.5 text-[13.5px] border border-[#E7E0D2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40 focus:border-[#3F4CE0]" />
            <button type="submit" disabled={typing} data-testid="chat-send" className="px-3.5 rounded-xl bg-[#12B76A] hover:bg-[#0E9355] text-white rp-btn disabled:opacity-50" aria-label="Send"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
}
